import { bytesToNDigits } from '@mail/cross-sizes-utils';
import React, { type ReactElement, memo, useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { WelcomeTrialParams } from 'reactApp/appHelpers/featuresHelpers/features.types';
import { PaymentUTM } from 'reactApp/constants/paymentUTM';
import { useSendSpaceMenuAnalytics } from 'reactApp/hooks/UseSendSpaceMenuAnalytics';
import { historyPush } from 'reactApp/modules/router/router.module';
import { getIdByStorage } from 'reactApp/modules/storage/storage.helpers';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { widgetTitles } from 'reactApp/modules/userQuotaCleaner/helpers/constants';
import { goToCleanerAndShowQuotaCleaner, loadUserQuotaCleanerList } from 'reactApp/modules/userQuotaCleaner/userQuotaCleaner.actions';
import { getFilteredLimitedGroups } from 'reactApp/modules/userQuotaCleaner/userQuotaCleaner.selectors';
import type { QuotaCleanerGroup } from 'reactApp/modules/userQuotaCleaner/userQuotaCleaner.types';
import { UserQuotaGroupId } from 'reactApp/types/QuotaCleaner';
import { type ClearItem, ClearBlock } from 'reactApp/ui/WelcomePromo/WelcomeNewPromo/ClearBlock';
import { TrialBlock } from 'reactApp/ui/WelcomePromo/WelcomeNewPromo/TrialBlock/TrialBlock';
import { useWelcomePromoLogic } from 'reactApp/ui/WelcomePromo/WelcomePromo.hooks';

import styles from '../WelcomeNewPromo/WelcomeNewPromo.css';

interface Props {
    space?: string;
    trialData?: WelcomeTrialParams | null;
    items: ClearItem[];

    onSubscriptionsClick(): void;

    onClearClick(): void;
}

export const WelcomeOverquotaPromoComponent = memo(({ onSubscriptionsClick, onClearClick, items = [], trialData }: Props): ReactElement => {
    return (
        <div className={styles.root}>
            <div className={styles.textBlock}>
                <div className={styles.header}>Ваше хранилище почти заполнено</div>
                <div className={styles.description}>
                    Чтобы{' '}
                    <span className={styles.link} onClick={onClearClick}>
                        освободить место
                    </span>{' '}
                    в хранилище, можно очистить ящик от тяжёлых
                    <br />
                    писем. Или увеличить хранилище, подключив один из{' '}
                    <span className={styles.link} onClick={onSubscriptionsClick}>
                        платных тарифов.
                    </span>
                </div>
            </div>
            <div className={styles.actionBlock}>
                <div className={styles.wrapper}>
                    <ClearBlock items={items} onClearClick={onClearClick} />
                    {trialData && <TrialBlock data={trialData} forQuota />}
                </div>
            </div>
        </div>
    );
});

WelcomeOverquotaPromoComponent.displayName = 'WelcomeOverquotaPromoComponent';

const MAX_CLEAR_ITEMS = 4;

const neededGroupsIds = [
    UserQuotaGroupId.Read,
    UserQuotaGroupId.LargeInbox,
    UserQuotaGroupId.LargeSent,
    UserQuotaGroupId.OldInbox,
    UserQuotaGroupId.MailBin,
    UserQuotaGroupId.MailSpam,
    UserQuotaGroupId.Newsletters,
    UserQuotaGroupId.OldSent,
    UserQuotaGroupId.WithAttaches,
];

export const WelcomeOverquotaPromo = memo((): ReactElement => {
    const dispatch = useDispatch();
    const { trialData, handleSubscriptionsClick } = useWelcomePromoLogic({ forNewUser: false, isOverquota: true });
    const groups = useSelector(getFilteredLimitedGroups);
    const sendAnalytics = useSendSpaceMenuAnalytics(false);

    const onClearItemClick = useCallback(
        (group: QuotaCleanerGroup) => {
            dispatch(
                goToCleanerAndShowQuotaCleaner({
                    groupId: group.groupId,
                    size: group.size,
                    from: 'welcomeOverquotaGroup',
                })
            );
        },
        [dispatch]
    );

    const items = useMemo(
        () =>
            groups
                .filter((group) => neededGroupsIds.includes(group.groupId))
                .filter((_, i) => i < MAX_CLEAR_ITEMS)
                .map((group) => ({
                    size: bytesToNDigits(group.size, 3).value,
                    title: widgetTitles[group.groupId],
                    onClick: () => onClearItemClick(group),
                })),
        [groups, onClearItemClick]
    );

    const handleClearClick = useCallback(() => {
        sendAnalytics('clear');
        dispatch(
            historyPush({
                id: getIdByStorage(EStorageType.quotaCleaner),
                search: `?${PaymentUTM.welcomeOverquota}`,
                hash: 'cleaner',
            })
        );
    }, []);

    useEffect(() => {
        dispatch(loadUserQuotaCleanerList());
    }, [dispatch]);

    return (
        <WelcomeOverquotaPromoComponent
            onSubscriptionsClick={handleSubscriptionsClick}
            onClearClick={handleClearClick}
            trialData={trialData}
            items={items}
        />
    );
});

WelcomeOverquotaPromo.displayName = 'WelcomeOverquotaPromo';
